<template>
  <div class="relative inline-block text-left z-50">
    <button 
      @click="isOpen = !isOpen"
      class="flex items-center justify-center w-10 h-10 rounded-full border border-[var(--pwa-border-strong)] bg-[var(--pwa-surface)] text-[var(--pwa-text)] hover:bg-[var(--pwa-surface-hover)] focus:outline-none transition-all duration-300 shadow-md"
      aria-label="Toggle theme menu"
    >
      <!-- Sun Icon (Active when light theme is active) -->
      <svg v-if="currentTheme === 'light'" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 transition-transform duration-500 rotate-0 scale-100">
        <path stroke-linecap="round" stroke-linejoin="round" d="M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z" />
      </svg>
      <!-- Moon Icon (Active when dark theme is active) -->
      <svg v-else-if="currentTheme === 'dark'" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 transition-transform duration-500 rotate-0 scale-100">
        <path stroke-linecap="round" stroke-linejoin="round" d="M21.752 15.002A9.72 9.72 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998Z" />
      </svg>
      <!-- System Icon (Active when system theme is active) -->
      <svg v-else xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 transition-transform duration-500 rotate-0 scale-100">
        <path stroke-linecap="round" stroke-linejoin="round" d="M9 17.25v1.007a3 3 0 0 1-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0 1 15 18.257V17.25m6-12V15a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 15V5.25m18 0A2.25 2.25 0 0 0 18.75 3H5.25A2.25 2.25 0 0 0 3 5.25m18 0V12a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 12V5.25" />
      </svg>
    </button>

    <!-- Dropdown Menu -->
    <transition
      enter-active-class="transition duration-100 ease-out"
      enter-from-class="transform scale-95 opacity-0"
      enter-to-class="transform scale-100 opacity-100"
      leave-active-class="transition duration-75 ease-in"
      leave-from-class="transform scale-100 opacity-100"
      leave-to-class="transform scale-95 opacity-0"
    >
      <div 
        v-if="isOpen" 
        class="absolute right-0 mt-2 w-36 rounded-xl border border-[var(--pwa-border-strong)] bg-[var(--pwa-surface)] shadow-lg overflow-hidden py-1"
      >
        <!-- Light Mode Button -->
        <button 
          @click="selectTheme('light')"
          class="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-[var(--pwa-text)] hover:bg-[var(--pwa-surface-hover)] transition-colors"
          :class="{ 'font-semibold text-[var(--pwa-accent)]': currentTheme === 'light' }"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z" />
          </svg>
          Light
        </button>

        <!-- Dark Mode Button -->
        <button 
          @click="selectTheme('dark')"
          class="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-[var(--pwa-text)] hover:bg-[var(--pwa-surface-hover)] transition-colors"
          :class="{ 'font-semibold text-[var(--pwa-accent)]': currentTheme === 'dark' }"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
            <path stroke-linecap="round" stroke-linejoin="round" d="M21.752 15.002A9.72 9.72 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998Z" />
          </svg>
          Dark
        </button>

        <!-- System Mode Button -->
        <button 
          @click="selectTheme('system')"
          class="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-[var(--pwa-text)] hover:bg-[var(--pwa-surface-hover)] transition-colors"
          :class="{ 'font-semibold text-[var(--pwa-accent)]': currentTheme === 'system' }"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 17.25v1.007a3 3 0 0 1-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0 1 15 18.257V17.25m6-12V15a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 15V5.25m18 0A2.25 2.25 0 0 0 18.75 3H5.25A2.25 2.25 0 0 0 3 5.25m18 0V12a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 12V5.25" />
          </svg>
          System
        </button>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';

const isOpen = ref(false);
const currentTheme = ref<'light' | 'dark' | 'system'>('system');

// Handle click outside to close dropdown
const closeDropdown = (e: MouseEvent) => {
  const target = e.target as HTMLElement;
  if (!target.closest('.z-50')) {
    isOpen.value = false;
  }
};

const applyTheme = (theme: 'light' | 'dark' | 'system') => {
  const root = document.documentElement;
  
  if (theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
    root.classList.remove('light');
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
    root.classList.add('light');
  }
};

const selectTheme = (theme: 'light' | 'dark' | 'system') => {
  currentTheme.value = theme;
  localStorage.setItem('pwa-theme', theme);
  applyTheme(theme);
  isOpen.value = false;
};

// Listen to system theme changes in real time
let mediaQueryList: MediaQueryList | null = null;
const handleSystemThemeChange = () => {
  if (currentTheme.value === 'system') {
    applyTheme('system');
  }
};

onMounted(() => {
  currentTheme.value = (localStorage.getItem('pwa-theme') || 'system') as 'light' | 'dark' | 'system';
  applyTheme(currentTheme.value);
  
  window.addEventListener('click', closeDropdown);
  
  // Set up listener for native device theme preference changes
  mediaQueryList = window.matchMedia('(prefers-color-scheme: dark)');
  mediaQueryList.addEventListener('change', handleSystemThemeChange);
});

onUnmounted(() => {
  window.removeEventListener('click', closeDropdown);
  if (mediaQueryList) {
    mediaQueryList.removeEventListener('change', handleSystemThemeChange);
  }
});
</script>
